import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Tooltip, Tag } from 'antd';
import {
  SearchOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { RevenuecatEventOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import { TextFilter } from '@/components/common/TextFilter';
import { ShowLink } from '@/components/common/ShowLink';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const RevenuecatEventList = () => {
  const t = useTranslate();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<RevenuecatEventOut>({
      pagination: { pageSize: 20 },
      resource: 'monetization.revenuecat_events',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(
                  'monetization.revenuecat_events.search.placeholder'
                )}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowLink resource="monetization.revenuecat_events" id={value} />
          )}
        />

        <Table.Column
          dataIndex="processed_at"
          title={t('monetization.revenuecat_events.fields.status')}
          sorter
          sortOrder={getSortOrder(sorters, 'processed_at')}
          render={(value: string | null) =>
            value ? (
              <Tooltip
                title={`${t('monetization.revenuecat_events.status.processed')}: ${new Date(value).toLocaleString()}`}
              >
                <Tag icon={<CheckCircleFilled />} color="success">
                  {t('monetization.revenuecat_events.status.processed')}
                </Tag>
              </Tooltip>
            ) : (
              <Tag icon={<ClockCircleOutlined />} color="warning">
                {t('monetization.revenuecat_events.status.pending')}
              </Tag>
            )
          }
        />

        <Table.Column
          dataIndex="event_id"
          title={t('monetization.revenuecat_events.fields.event_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'event_id')}
          render={(value: string) => (
            <span style={{ fontFamily: 'monospace' }}>{value}</span>
          )}
        />

        <Table.Column
          dataIndex="event_type"
          title={t('monetization.revenuecat_events.fields.event_type')}
          sorter
          sortOrder={getSortOrder(sorters, 'event_type')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />

        <Table.Column
          dataIndex="app_user_id"
          title={t('monetization.revenuecat_events.fields.app_user_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'app_user_id')}
          render={(value: string | null) => {
            if (!value) return '-';
            return (
              <RevenueCatCustomerLink
                id={value}
                title={t('monetization.customers.actions.open_in_revenuecat')}
                stopRowClick
              />
            );
          }}
        />

        <Table.Column
          dataIndex="environment"
          title={t('monetization.revenuecat_events.fields.environment')}
          sorter
          sortOrder={getSortOrder(sorters, 'environment')}
          render={(value: string | null) => {
            if (!value) return '-';
            const color = value === 'PRODUCTION' ? 'success' : 'warning';
            return <Tag color={color}>{value}</Tag>;
          }}
        />

        <Table.Column
          dataIndex="store"
          title={t('monetization.revenuecat_events.fields.store')}
          sorter
          sortOrder={getSortOrder(sorters, 'store')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />

        <Table.Column
          dataIndex="product_id"
          title={t('monetization.revenuecat_events.fields.product_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_id')}
          render={(value: string | null) => value ?? '-'}
        />

        <Table.Column
          dataIndex="period_type"
          title={t('monetization.revenuecat_events.fields.period_type')}
          sorter
          sortOrder={getSortOrder(sorters, 'period_type')}
          render={(value: string | null) => value ?? '-'}
        />

        <Table.Column
          dataIndex="transaction_id"
          title={t('monetization.revenuecat_events.fields.transaction_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'transaction_id')}
          render={(value: string | null) =>
            value ? (
              <span style={{ fontFamily: 'monospace' }}>{value}</span>
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'transaction_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="transaction_id"
              width={220}
              placeholder={t(
                'monetization.revenuecat_events.filter.transaction_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="event_timestamp"
          title={t('monetization.revenuecat_events.fields.event_timestamp')}
          sorter
          sortOrder={getSortOrder(sorters, 'event_timestamp')}
          render={(value: string | null) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          dataIndex="created_at"
          title={t('monetization.revenuecat_events.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
